// Pharmacy-only guidance. Existing URLs and screenshot assets are retained.
// Demo procedures never establish Production approval or live payment readiness.
export const PHARMACY_HELP_CATEGORIES = [
  {
    "slug": "getting-started",
    "title": "Getting started",
    "audience": "pharmacy",
    "summary": "Use your existing pharmacy account and confirm the branch before opening its workspace.",
    "icon": "Compass",
    "status": "Guide available",
    "articles": [
      {
        "slug": "pharmacy-dashboard-quick-start",
        "title": "Pharmacy dashboard quick start",
        "summary": "Use your existing pharmacy account and confirm the branch before opening its workspace.",
        "steps": [
          "Sign in at /pharmacy with the email linked to your owner or accepted branch-member record and your password.",
          "For first-time password setup or recovery, use the secure-link option if available, then My account → Password and sign-in.",
          "Check My account for your branch and branch role. A guide or a saved browser profile does not grant branch access.",
          "Open Dashboard, Inventory or the prescription queue for the linked branch. If access is denied, ask the branch owner or support to check the account link.",
          "After signing in from a Help link, return to the selected guide. Do not create a replacement account to bypass an access restriction."
        ],
        "callout": {
          "tone": "trust",
          "title": "Account and branch access",
          "body": "Production Pharmacy access remains subject to its separate release and approval. Staging sign-in does not establish Production access."
        },
        "screenshots": [
          {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-staging-entry.webp",
            "label": "Pharmacy staging entry",
            "caption": "Use the invited pharmacy email and continue into the pharmacy onboarding route.",
            "alt": "AfriMed pharmacy staging entry and sign-in screens."
          },
          {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-dashboard-inventory.webp",
            "label": "Dashboard and inventory preview",
            "caption": "After launch, seeded demo rows make the dashboard and inventory understandable immediately.",
            "alt": "AfriMed pharmacy dashboard and inventory screens with seeded demo data."
          }
        ],
        "demoSteps": [
          "Invited Staging owners may complete the test onboarding route. Seeded stock and sample requests are demonstration data, not evidence of live service approval."
        ],
        "fr": {
          "title": "Démarrage rapide du tableau de bord pharmacie",
          "summary": "Utilisez votre compte pharmacie existant et vérifiez l’officine avant d’ouvrir son espace.",
          "steps": [
            "Connectez-vous sur /pharmacy avec l’adresse liée à votre dossier de propriétaire ou de membre accepté et votre mot de passe.",
            "Pour définir ou récupérer votre mot de passe, utilisez le lien sécurisé si disponible, puis Mon compte → Mot de passe et connexion.",
            "Vérifiez votre officine et votre rôle dans Mon compte. Un guide ou un profil enregistré dans le navigateur ne donne pas accès à l’officine.",
            "Ouvrez le tableau de bord, le stock ou la file des prescriptions de l’officine liée. Si l’accès est refusé, demandez au propriétaire ou à l’assistance de vérifier le rattachement du compte.",
            "Après une connexion depuis un lien d’aide, revenez au guide choisi. Ne créez pas un autre compte pour contourner une restriction."
          ],
          "demoSteps": [
            "Les propriétaires invités en Staging peuvent suivre le parcours de configuration de test. Le stock et les demandes exemples sont des données de démonstration, pas une approbation de service réel."
          ],
          "callout": {
            "tone": "trust",
            "title": "Accès au compte et à l’officine",
            "body": "L’accès Pharmacie en Production reste soumis à sa publication et à son approbation séparées. Une connexion en Staging ne prouve pas l’accès en Production."
          },
          "screenshots": [
            {
              "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-staging-entry.webp",
              "label": "Entrée pharmacie en Staging",
              "caption": "Connexion à la pharmacie dans la démonstration. La connexion habituelle utilise le mot de passe.",
              "alt": "Entrée pharmacie en Staging — capture de démonstration AfriMed™."
            },
            {
              "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-dashboard-inventory.webp",
              "label": "Tableau de bord et stock",
              "caption": "Écrans de démonstration du tableau de bord et du stock de l’officine.",
              "alt": "Tableau de bord et stock — capture de démonstration AfriMed™."
            }
          ]
        }
      }
    ],
    "fr": {
      "title": "Premiers pas",
      "summary": "Utilisez votre compte pharmacie existant et vérifiez l’officine avant d’ouvrir son espace.",
      "status": "Guide disponible"
    }
  },
  {
    "slug": "pharmacy-profile-verification",
    "title": "Pharmacy profile & verification",
    "audience": "pharmacy",
    "summary": "Review branch identity, responsible staff and verification status without treating pending checks as approval.",
    "icon": "ShieldCheck",
    "status": "Guide available",
    "articles": [
      {
        "slug": "complete-staging-onboarding",
        "title": "Review pharmacy profile and verification",
        "summary": "Review branch identity, responsible staff and verification status without treating pending checks as approval.",
        "steps": [
          "The branch owner completes pharmacy identity and operations details in onboarding. Existing members use the branch linked to their invitation.",
          "Check the pharmacy name, address, responsible pharmacist, licence and registration details against the branch’s actual records.",
          "Review the fulfilment modes and verification uploads requested by the form. Do not use test placeholders in an operational submission.",
          "Review Settings for branch verification details and My account for your own member identity. These are different records.",
          "Keep verification, public branch availability and settlement readiness separate. A dashboard opening is not approval to dispense or receive payouts."
        ],
        "callout": {
          "tone": "trust",
          "title": "Pending does not mean approved",
          "body": "Do not connect a live payment account or submit real patient data as part of a demonstration."
        },
        "screenshots": [
          {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-onboarding-1-3.webp",
            "label": "Onboarding steps 1 to 3",
            "caption": "Capture branch identity, operation basics, and staging verification status.",
            "alt": "AfriMed pharmacy onboarding screens for the first three pharmacy setup steps."
          },
          {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-onboarding-4-5.webp",
            "label": "Onboarding steps 4 to 5",
            "caption": "Review role handoff, pending readiness, and the launch step before opening the dashboard.",
            "alt": "AfriMed pharmacy onboarding screens for role handoff and dashboard launch."
          },
          {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-account.webp",
            "label": "Pharmacy account",
            "caption": "Use the account area to review branch identity and workspace preferences after launch.",
            "alt": "AfriMed pharmacy account screen."
          }
        ],
        "demoSteps": [
          "Only in Staging, use approved test placeholders and leave verification or payout checks pending where the test workflow permits. The retained screenshots show that demo sequence."
        ],
        "fr": {
          "title": "Vérifier le profil et la vérification de l’officine",
          "summary": "Vérifiez l’identité de l’officine, ses responsables et son statut sans considérer les contrôles en attente comme une approbation.",
          "steps": [
            "Le propriétaire renseigne l’identité et les détails opérationnels lors de la configuration. Les membres existants utilisent l’officine liée à leur invitation.",
            "Vérifiez le nom, l’adresse, le pharmacien responsable, l’autorisation et l’enregistrement à partir des documents réels de l’officine.",
            "Vérifiez les modes d’exécution et les justificatifs demandés. N’utilisez pas de données fictives dans un dossier opérationnel.",
            "Consultez les réglages pour la vérification de l’officine et Mon compte pour votre identité de membre. Ce sont deux dossiers distincts.",
            "Distinguez vérification, visibilité publique et préparation des versements. L’ouverture du tableau de bord n’autorise ni la délivrance ni les versements."
          ],
          "demoSteps": [
            "En Staging uniquement, utilisez les données de test approuvées et laissez les contrôles en attente lorsque le parcours de test le permet. Les captures conservées montrent cette séquence de démonstration."
          ],
          "callout": {
            "tone": "trust",
            "title": "En attente ne signifie pas approuvé",
            "body": "Ne connectez pas de compte de paiement réel et ne transmettez pas de données patient réelles dans une démonstration."
          },
          "screenshots": [
            {
              "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-onboarding-1-3.webp",
              "label": "Configuration, étapes 1 à 3",
              "caption": "Identité de l’officine, opérations et vérification dans le parcours de démonstration.",
              "alt": "Configuration, étapes 1 à 3 — capture de démonstration AfriMed™."
            },
            {
              "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-onboarding-4-5.webp",
              "label": "Configuration, étapes 4 à 5",
              "caption": "Rôles, contrôles en attente et ouverture du tableau de bord en démonstration.",
              "alt": "Configuration, étapes 4 à 5 — capture de démonstration AfriMed™."
            },
            {
              "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-account.webp",
              "label": "Compte pharmacie",
              "caption": "Identité du membre et rôle dans l’officine sur la capture de démonstration.",
              "alt": "Compte pharmacie — capture de démonstration AfriMed™."
            }
          ]
        }
      }
    ],
    "fr": {
      "title": "Profil pharmacie et vérification",
      "summary": "Vérifiez l’identité de l’officine, ses responsables et son statut sans considérer les contrôles en attente comme une approbation.",
      "status": "Guide disponible"
    }
  },
  {
    "slug": "inventory-management",
    "title": "Inventory management",
    "audience": "pharmacy",
    "summary": "Use the linked branch inventory and confirm that saved changes persist.",
    "icon": "Pill",
    "status": "Guide available",
    "articles": [
      {
        "slug": "seeded-stock-and-catalogue",
        "title": "Review stock and update inventory",
        "summary": "Use the linked branch inventory and confirm that saved changes persist.",
        "steps": [
          "Open Inventory at /pharmacy/inventory and confirm the branch context before editing.",
          "Use Add stock item or open an existing item to edit it, within the stock responsibilities assigned by your branch.",
          "Check the item, strength, stock count, reorder level, price and currency, batch, expiry and storage details.",
          "Save, then reopen the item to confirm the change. If saving fails, retain the error and do not assume the stock was updated.",
          "Check low-stock alerts and prescription availability against the saved branch stock; report missing rows or another branch’s data."
        ],
        "callout": {
          "tone": "trust",
          "title": "Branch-scoped stock",
          "body": "Access is tied to the owner or active linked membership. A role description alone does not prove that every stock action is restricted by that role."
        },
        "screenshot": {
          "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-dashboard-inventory.webp",
          "label": "Dashboard and inventory",
          "caption": "Inventory and dashboard cards should hydrate from the same pharmacy branch scope.",
          "alt": "AfriMed pharmacy dashboard and inventory screens showing seeded data."
        },
        "demoSteps": [
          "Seeded stock is for Staging exercises only. Use approved synthetic items there; do not interpret demonstration quantities as real availability."
        ],
        "fr": {
          "title": "Consulter et mettre à jour le stock",
          "summary": "Utilisez le stock de l’officine liée et vérifiez que les modifications sont enregistrées.",
          "steps": [
            "Ouvrez le stock sur /pharmacy/inventory et vérifiez l’officine avant toute modification.",
            "Utilisez Ajouter un article ou ouvrez un article existant, dans le cadre des responsabilités de stock confiées par votre officine.",
            "Vérifiez l’article, le dosage, la quantité, le seuil de réapprovisionnement, le prix et la devise, le lot, la péremption et le stockage.",
            "Enregistrez puis rouvrez l’article pour vérifier la modification. En cas d’échec, conservez l’erreur sans supposer que le stock a changé.",
            "Comparez les alertes de stock et la disponibilité pour les prescriptions au stock enregistré ; signalez les lignes manquantes ou les données d’une autre officine."
          ],
          "demoSteps": [
            "Le stock de démonstration sert uniquement aux exercices en Staging. Utilisez-y des articles fictifs approuvés ; ces quantités ne prouvent pas une disponibilité réelle."
          ],
          "callout": {
            "tone": "trust",
            "title": "Stock propre à l’officine",
            "body": "L’accès dépend du propriétaire ou d’une adhésion active liée. Une description de rôle ne prouve pas à elle seule que chaque action de stock est restreinte selon ce rôle."
          },
          "screenshot": {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-dashboard-inventory.webp",
            "label": "Tableau de bord et stock",
            "caption": "Écrans de démonstration du tableau de bord et du stock de l’officine.",
            "alt": "Tableau de bord et stock — capture de démonstration AfriMed™."
          }
        }
      }
    ],
    "fr": {
      "title": "Gestion du stock",
      "summary": "Utilisez le stock de l’officine liée et vérifiez que les modifications sont enregistrées.",
      "status": "Guide disponible"
    }
  },
  {
    "slug": "prescription-fulfilment",
    "title": "Prescription fulfilment",
    "audience": "pharmacy",
    "summary": "Follow the request’s recorded state and the actions available for the linked branch.",
    "icon": "ClipboardList",
    "status": "Guide available",
    "articles": [
      {
        "slug": "fulfilment-playbook",
        "title": "Verify, price, prepare, and hand off",
        "summary": "Follow the request’s recorded state and the actions available for the linked branch.",
        "steps": [
          "Open /pharmacy/prescriptions and use Open order to review the prescription and branch assignment.",
          "Check the issued prescription and stock. Accept or reject a newly selected request using the available action.",
          "For a preparing request, set the price and any delivery details required by the form. Confirm the saved amount and currency.",
          "An awaiting-payment state is not payment confirmation. Mark ready for pickup only when the recorded state and available action permit it.",
          "Review the order’s saved status after each action. Do not change the prescriber’s instructions or promise a handoff that has not been recorded."
        ],
        "callout": {
          "tone": "trust",
          "title": "Clinical and branch responsibility",
          "body": "Only appropriately authorised staff should perform dispensing tasks. Guide access does not authorise prescribing, substitutions, or another branch’s fulfilment."
        },
        "screenshots": [
          {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-playbook.webp",
            "label": "Fulfilment playbook",
            "caption": "Use the playbook to keep fulfilment actions ordered and safe.",
            "alt": "AfriMed pharmacy fulfilment playbook."
          },
          {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-playbook-1.webp",
            "label": "Prescription fulfilment request",
            "caption": "Open a seeded request and move it through the branch workflow.",
            "alt": "AfriMed pharmacy prescription fulfilment guide."
          },
          {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-prescription-open.webp",
            "label": "Prescription detail",
            "caption": "Review prescription context before confirming availability or fulfilment actions.",
            "alt": "AfriMed pharmacy prescription detail screen."
          }
        ],
        "demoSteps": [
          "Use seeded requests and synthetic prices only for a Staging rehearsal. A demo paid state is not a real payment or authorisation to dispense."
        ],
        "fr": {
          "title": "Vérifier, tarifer, préparer et remettre",
          "summary": "Suivez l’état enregistré de la demande et les actions disponibles pour l’officine liée.",
          "steps": [
            "Ouvrez /pharmacy/prescriptions puis Ouvrir la commande pour vérifier la prescription et l’officine destinataire.",
            "Vérifiez la prescription émise et le stock. Acceptez ou refusez une nouvelle demande attribuée à l’aide de l’action disponible.",
            "Pour une demande en préparation, fixez le prix et les détails de livraison requis. Vérifiez le montant et la devise enregistrés.",
            "L’état En attente de paiement ne confirme pas le paiement. Marquez Prêt à retirer uniquement lorsque l’état enregistré et l’action disponible le permettent.",
            "Vérifiez l’état enregistré après chaque action. Ne modifiez pas les instructions du prescripteur et ne promettez pas une remise non enregistrée."
          ],
          "demoSteps": [
            "Utilisez des demandes exemples et des prix fictifs pour les essais en Staging. Un état payé de démonstration n’est ni un paiement réel ni une autorisation de délivrance."
          ],
          "callout": {
            "tone": "trust",
            "title": "Responsabilité clinique et de l’officine",
            "body": "Seul le personnel dûment autorisé doit effectuer les tâches de délivrance. L’accès au guide n’autorise ni prescription, ni substitution, ni exécution pour une autre officine."
          },
          "screenshots": [
            {
              "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-playbook.webp",
              "label": "Guide d’exécution",
              "caption": "Séquence d’exécution d’une prescription dans la démonstration.",
              "alt": "Guide d’exécution — capture de démonstration AfriMed™."
            },
            {
              "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-playbook-1.webp",
              "label": "Demande d’exécution",
              "caption": "Exemple de demande de prescription attribuée à l’officine.",
              "alt": "Demande d’exécution — capture de démonstration AfriMed™."
            },
            {
              "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-prescription-open.webp",
              "label": "Détail de la prescription",
              "caption": "Contexte de la prescription avant les actions d’exécution.",
              "alt": "Détail de la prescription — capture de démonstration AfriMed™."
            }
          ]
        }
      }
    ],
    "fr": {
      "title": "Exécution des prescriptions",
      "summary": "Suivez l’état enregistré de la demande et les actions disponibles pour l’officine liée.",
      "status": "Guide disponible"
    }
  },
  {
    "slug": "payments-settlement",
    "title": "Payments & settlement",
    "audience": "pharmacy",
    "summary": "Distinguish recorded transactions, revenue and settlement availability.",
    "icon": "CreditCard",
    "status": "Guide available",
    "articles": [
      {
        "slug": "staging-payments-and-revenue",
        "title": "Review payments and settlement readiness",
        "summary": "Distinguish recorded transactions, revenue and settlement availability.",
        "steps": [
          "Open Payments at /pharmacy/payments for the linked branch.",
          "Review transaction status, amount and currency. A quote or pending transaction is not paid revenue.",
          "Compare paid transaction rows with the dashboard totals for the same branch, period and currency.",
          "Use payment or settlement actions only when they are enabled for the released service and your account. Do not force a payment link or retry a held payment flow.",
          "Ask the branch owner or support about settlement readiness. A paid row alone does not establish a payout or an approved bank account."
        ],
        "callout": {
          "tone": "trust",
          "title": "Live payment release is separate",
          "body": "Live payment actions remain restricted. An available guide does not mean that payments or settlement are enabled for your branch."
        },
        "screenshot": {
          "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-payments-messages.webp",
          "label": "Payments and Messages",
          "caption": "Payments and Messages should stay in pharmacy context and never route into admin dashboards.",
          "alt": "AfriMed pharmacy payments and messages screens."
        },
        "demoSteps": [
          "Staging fake paid rows exercise reporting only. Do not connect a live provider, capture funds or treat those rows as settlement."
        ],
        "fr": {
          "title": "Consulter les paiements et la préparation des versements",
          "summary": "Distinguez les transactions enregistrées, les revenus et la disponibilité des versements.",
          "steps": [
            "Ouvrez Paiements sur /pharmacy/payments pour l’officine liée.",
            "Vérifiez le statut, le montant et la devise. Un devis ou une transaction en attente n’est pas un revenu payé.",
            "Comparez les transactions payées aux totaux du tableau de bord pour la même officine, la même période et la même devise.",
            "Utilisez les actions de paiement ou de versement uniquement si elles sont activées pour le service publié et votre compte. Ne forcez pas un lien de paiement et ne relancez pas un parcours suspendu.",
            "Demandez au propriétaire ou à l’assistance de vérifier la préparation des versements. Une ligne payée ne prouve ni un versement ni l’approbation d’un compte bancaire."
          ],
          "demoSteps": [
            "Les lignes payées fictives de Staging servent uniquement à tester les rapports. Ne connectez pas de prestataire réel, ne débitez pas de fonds et ne considérez pas ces lignes comme des versements."
          ],
          "callout": {
            "tone": "trust",
            "title": "Publication des paiements réels séparée",
            "body": "Les écritures de paiement en Production restent restreintes. Ce guide n’active aucun prestataire et ne confirme pas la préparation des versements réels."
          },
          "screenshot": {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-payments-messages.webp",
            "label": "Paiements et messages",
            "caption": "Écrans de démonstration des paiements et des messages de l’officine.",
            "alt": "Paiements et messages — capture de démonstration AfriMed™."
          }
        }
      }
    ],
    "fr": {
      "title": "Paiements et règlement",
      "summary": "Distinguez les transactions enregistrées, les revenus et la disponibilité des versements.",
      "status": "Guide disponible"
    }
  },
  {
    "slug": "messages-fola",
    "title": "Messages & Fola™",
    "audience": "pharmacy",
    "summary": "Keep branch conversations, assistant guidance and support requests distinct.",
    "icon": "MessageCircle",
    "status": "Guide available",
    "articles": [
      {
        "slug": "pharmacy-fola-safe-prompts",
        "title": "Use pharmacy messages and bounded Fola™ guidance",
        "summary": "Keep branch conversations, assistant guidance and support requests distinct.",
        "steps": [
          "Open Messages at /pharmacy/messages and check the conversation and branch context before sending.",
          "Use only conversations and patient context that your account is authorised to access.",
          "If Fola™ is available, use pharmacy operations prompts and check its response against the actual screen and branch procedure.",
          "Do not use Fola™ for diagnosis, medication substitution, prescribing, changing permissions or confirming payment.",
          "Use Contact support for a support request and its tracked replies. Sending a message to Fola™ does not create a support ticket."
        ],
        "callout": {
          "tone": "trust",
          "title": "Assistant guidance is not an action",
          "body": "Fola™ cannot replace professional judgement or the recorded result of a branch action. If unavailable, use the guide or existing support path."
        },
        "screenshot": {
          "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-payments-messages.webp",
          "label": "Messages and Fola™ entry",
          "caption": "Messages and Fola™ remain inside pharmacy-safe routes.",
          "alt": "AfriMed pharmacy messages and Fola screens."
        },
        "demoSteps": [
          "Use synthetic prompts in Staging. A working demonstration or visible prompt does not establish Production assistant or API availability."
        ],
        "fr": {
          "title": "Utiliser les messages pharmacie et l’aide limitée de Fola™",
          "summary": "Distinguez les conversations de l’officine, l’aide de l’assistant et les demandes d’assistance.",
          "steps": [
            "Ouvrez Messages sur /pharmacy/messages et vérifiez la conversation et l’officine avant d’envoyer.",
            "Utilisez uniquement les conversations et le contexte patient auxquels votre compte est autorisé à accéder.",
            "Si Fola™ est disponible, utilisez les questions sur les opérations de pharmacie et comparez la réponse à l’écran réel et à la procédure de l’officine.",
            "N’utilisez pas Fola™ pour diagnostiquer, substituer un médicament, prescrire, modifier les permissions ou confirmer un paiement.",
            "Utilisez Contacter l’assistance pour créer une demande et suivre ses réponses. Un message à Fola™ ne crée pas de ticket."
          ],
          "demoSteps": [
            "Utilisez des questions fictives en Staging. Une démonstration fonctionnelle ou une question proposée ne prouve pas la disponibilité de l’assistant ou de l’API en Production."
          ],
          "callout": {
            "tone": "trust",
            "title": "L’aide de l’assistant n’est pas une action",
            "body": "Fola™ ne remplace ni le jugement professionnel ni le résultat enregistré d’une action de l’officine. En cas d’indisponibilité, utilisez le guide ou l’assistance existante."
          },
          "screenshot": {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-payments-messages.webp",
            "label": "Paiements et messages",
            "caption": "Écrans de démonstration des paiements et des messages de l’officine.",
            "alt": "Paiements et messages — capture de démonstration AfriMed™."
          }
        }
      }
    ],
    "fr": {
      "title": "Messages et Fola™",
      "summary": "Distinguez les conversations de l’officine, l’aide de l’assistant et les demandes d’assistance.",
      "status": "Guide disponible"
    }
  },
  {
    "slug": "team-members-permissions",
    "title": "Team members & permissions",
    "audience": "pharmacy",
    "summary": "Keep invitations with the branch owner and review each member’s recorded role.",
    "icon": "Users",
    "status": "Guide available",
    "articles": [
      {
        "slug": "invite-staff-and-assign-roles",
        "title": "Invite staff and review branch roles",
        "summary": "Keep invitations with the branch owner and review each member’s recorded role.",
        "steps": [
          "The branch owner opens Settings → Members at /pharmacy/settings to review the team.",
          "Check the intended work email and branch before creating an invitation. Select the smallest role appropriate to the person’s responsibilities.",
          "Check whether the invitation was saved and whether sending succeeded. A copied link or pending invite does not mean the member has accepted.",
          "The member signs in with the invited address and checks their role and branch in My account. Report incorrect or revoked access to the owner.",
          "Keep ownership, staff access and payout settings with the authorised owner. Staff should stay within their assigned dispensing, inventory, finance, delivery or branch-support responsibilities."
        ],
        "callout": {
          "tone": "trust",
          "title": "Role descriptions and enforced access",
          "body": "Follow the responsibilities assigned by your branch. If a sensitive action appears outside your role, stop and ask the owner or support to confirm your access."
        },
        "screenshot": {
          "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-account.webp",
          "label": "Pharmacy account and permissions",
          "caption": "Review owner/member identity and privilege handoff before inviting staff.",
          "alt": "AfriMed pharmacy account and permission screen."
        },
        "demoSteps": [
          "Use existing approved test members for Staging checks. Do not create or reassign accounts merely to obtain access to a guide."
        ],
        "fr": {
          "title": "Inviter l’équipe et vérifier les rôles de l’officine",
          "summary": "Confiez les invitations au propriétaire et vérifiez le rôle enregistré de chaque membre.",
          "steps": [
            "Le propriétaire ouvre Réglages → Membres sur /pharmacy/settings pour consulter l’équipe.",
            "Vérifiez l’adresse professionnelle et l’officine avant de créer une invitation. Choisissez le rôle minimal adapté aux responsabilités de la personne.",
            "Vérifiez l’enregistrement de l’invitation et la réussite de l’envoi. Un lien copié ou une invitation en attente ne signifie pas que le membre a accepté.",
            "Le membre se connecte avec l’adresse invitée et vérifie son rôle et son officine dans Mon compte. Signalez au propriétaire tout accès incorrect ou révoqué.",
            "Réservez la propriété, l’accès du personnel et les réglages de versement au propriétaire autorisé. Chaque membre reste dans ses responsabilités de délivrance, stock, finances, livraison ou soutien à l’officine."
          ],
          "demoSteps": [
            "Utilisez les membres de test existants et approuvés en Staging. Ne créez pas de compte et ne réattribuez pas de rôle simplement pour accéder à un guide."
          ],
          "callout": {
            "tone": "trust",
            "title": "Descriptions des rôles et accès contrôlé",
            "body": "Respectez les responsabilités attribuées par votre officine. Si une action sensible apparaît hors de votre rôle, arrêtez-vous et demandez au propriétaire ou à l’assistance de confirmer votre accès."
          },
          "screenshot": {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-account.webp",
            "label": "Compte pharmacie",
            "caption": "Identité du membre et rôle dans l’officine sur la capture de démonstration.",
            "alt": "Compte pharmacie — capture de démonstration AfriMed™."
          }
        }
      }
    ],
    "fr": {
      "title": "Équipe et permissions",
      "summary": "Confiez les invitations au propriétaire et vérifiez le rôle enregistré de chaque membre.",
      "status": "Guide disponible"
    }
  },
  {
    "slug": "notifications-alerts",
    "title": "Notifications & alerts",
    "audience": "pharmacy",
    "summary": "Use alerts to locate work, then check the underlying prescription or stock record.",
    "icon": "Bell",
    "status": "Guide available",
    "articles": [
      {
        "slug": "notification-counts-and-alerts",
        "title": "Check prescription and stock alerts",
        "summary": "Use alerts to locate work, then check the underlying prescription or stock record.",
        "steps": [
          "Open the notification bell and review unread alerts for the linked branch.",
          "Follow prescription alerts to the queue and stock alerts to Inventory. Confirm the current record before acting.",
          "The bell counts unread alerts, including stock warnings. It need not equal the number of prescriptions or the dashboard totals.",
          "Reading an alert changes its unread state; it does not fulfil a prescription or update stock.",
          "If an alert is stale or belongs to another branch, record the route and time and contact support."
        ],
        "callout": {
          "tone": "trust",
          "title": "Alerts are prompts to review",
          "body": "In-app alerts are not a guaranteed external notification or confirmation that an operational action completed."
        },
        "screenshot": {
          "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-dashboard-inventory.webp",
          "label": "Dashboard notifications and metrics",
          "caption": "Dashboard metrics and inventory alerts should remain stable across refresh and navigation.",
          "alt": "AfriMed pharmacy dashboard and inventory notification state."
        },
        "demoSteps": [
          "Seeded Staging records may generate sample alerts. Compare the linked sample record, not an assumed equality between all counters."
        ],
        "fr": {
          "title": "Vérifier les alertes de prescription et de stock",
          "summary": "Utilisez les alertes pour repérer le travail, puis vérifiez la prescription ou l’article concerné.",
          "steps": [
            "Ouvrez la cloche et consultez les alertes non lues de l’officine liée.",
            "Suivez les alertes de prescription vers la file et celles de stock vers le stock. Vérifiez le dossier actuel avant d’agir.",
            "La cloche compte les alertes non lues, y compris celles de stock. Ce nombre peut différer du nombre de prescriptions et des totaux du tableau de bord.",
            "Lire une alerte change son état de lecture ; cela n’exécute pas une prescription et ne modifie pas le stock.",
            "Si une alerte est périmée ou concerne une autre officine, notez le chemin et l’heure puis contactez l’assistance."
          ],
          "demoSteps": [
            "Les données exemples de Staging peuvent générer des alertes. Comparez le dossier exemple lié sans supposer que tous les compteurs doivent être égaux."
          ],
          "callout": {
            "tone": "trust",
            "title": "Les alertes invitent à vérifier",
            "body": "Les alertes dans l’application ne garantissent ni une notification externe ni la réalisation d’une action opérationnelle."
          },
          "screenshot": {
            "src": "/assets/FAQ/onboarding/pharmacy/mockups/pharmacy-dashboard-inventory.webp",
            "label": "Tableau de bord et stock",
            "caption": "Écrans de démonstration du tableau de bord et du stock de l’officine.",
            "alt": "Tableau de bord et stock — capture de démonstration AfriMed™."
          }
        }
      }
    ],
    "fr": {
      "title": "Notifications et alertes",
      "summary": "Utilisez les alertes pour repérer le travail, puis vérifiez la prescription ou l’article concerné.",
      "status": "Guide disponible"
    }
  },
  {
    "slug": "technical-support",
    "title": "Technical support",
    "audience": "pharmacy",
    "summary": "Use the existing support queue and provide enough context without exposing patient information.",
    "icon": "LifeBuoy",
    "status": "Guide available",
    "articles": [
      {
        "slug": "report-staging-pharmacy-issue",
        "publicSupportLink": true,
        "title": "Report a pharmacy issue",
        "summary": "Use the existing support queue and provide enough context without exposing patient information.",
        "steps": [
          "Use Contact support to open /help-support with your language and the technical-issue topic.",
          "Record the route, time, branch role and whether the issue affects sign-in, inventory, prescriptions, payments, messages or notifications.",
          "Describe the expected and observed result. Remove patient information, passwords and codes from any supporting screenshot.",
          "After submission, keep the request ID and open the request to read replies and its current status. Reply in that same request.",
          "If you cannot sign in or the support page is unavailable, choose Support below to visit our Contact page. Email replies remain separate from your in-app ticket history."
        ],
        "callout": {
          "tone": "trust",
          "title": "Support is not emergency care",
          "body": "Use the appropriate clinical or emergency route for urgent care. Do not send medical records or passwords to the general inbox."
        },
        "demoSteps": [
          "Label reports from Staging as demonstration issues. Preserve existing requests and accounts when reporting access failures."
        ],
        "fr": {
          "title": "Signaler un problème pharmacie",
          "summary": "Utilisez la file d’assistance existante et fournissez le contexte nécessaire sans exposer de données patient.",
          "steps": [
            "Utilisez Contacter l’assistance pour ouvrir /help-support avec votre langue et le sujet Problème technique.",
            "Notez le chemin, l’heure, votre rôle et la fonction concernée : connexion, stock, prescriptions, paiements, messages ou notifications.",
            "Décrivez le résultat attendu et observé. Masquez les données patient, mots de passe et codes dans toute capture jointe au signalement.",
            "Après l’envoi, conservez l’identifiant et ouvrez la demande pour lire ses réponses et son statut. Répondez dans la même demande.",
            "Si vous ne pouvez pas vous connecter ou ouvrir l’assistance, choisissez Assistance ci-dessous pour accéder à notre page Contact. Les réponses par e-mail restent distinctes de votre historique dans l’application."
          ],
          "demoSteps": [
            "Précisez qu’un signalement provenant de Staging concerne la démonstration. Conservez les demandes et comptes existants en cas de problème d’accès."
          ],
          "callout": {
            "tone": "trust",
            "title": "L’assistance n’est pas un service d’urgence",
            "body": "Pour des soins urgents, utilisez le parcours clinique ou d’urgence approprié. N’envoyez ni dossier médical ni mot de passe à la boîte générale."
          }
        }
      }
    ],
    "fr": {
      "title": "Assistance technique",
      "summary": "Utilisez la file d’assistance existante et fournissez le contexte nécessaire sans exposer de données patient.",
      "status": "Guide disponible"
    }
  }
];
